import React, { useEffect, useState } from 'react';
import { Search, ChevronRight, Sparkles, FileText, Plus } from 'lucide-react';
import C from '../../constants/colors';
import * as historyApi from '../../api/history';
import * as tenantSettingsApi from '../../api/tenantSettings';
import KBPromotionModal from '../modals/KBPromotionModal';

export default function HistorySearchView({ sites, zones, onPromoted }) {
  const [query, setQuery] = useState('');
  const [siteId, setSiteId] = useState('');
  const [workType, setWorkType] = useState('');
  const [kbResults, setKbResults] = useState(null);     // null = not searched; [] = searched, empty
  const [repoResults, setRepoResults] = useState(null); // same convention
  const [synthesis, setSynthesis] = useState(null);
  const [loadingKb, setLoadingKb] = useState(false);
  const [loadingRepo, setLoadingRepo] = useState(false);
  const [settings, setSettings] = useState({ synthesisEnabled: false });
  const [synthesisToggle, setSynthesisToggle] = useState(false);
  const [promotionTarget, setPromotionTarget] = useState(null); // { snippet }

  useEffect(() => {
    tenantSettingsApi.getHseSettings().then(s => {
      setSettings(s);
      setSynthesisToggle(!!s.synthesisEnabled);
    }).catch(() => {});
  }, []);

  async function runKbSearch() {
    if (!query.trim()) return;
    setLoadingKb(true);
    setKbResults(null);
    setRepoResults(null);
    setSynthesis(null);
    try {
      const data = await historyApi.searchKb({
        query, siteId: siteId || undefined, workType: workType || undefined,
      });
      setKbResults(data.results || []);
    } catch (err) { alert('KB search failed: ' + (err?.response?.data?.error || err.message)); }
    finally { setLoadingKb(false); }
  }

  async function runRepoSearch() {
    if (!query.trim()) return;
    setLoadingRepo(true);
    setRepoResults(null);
    setSynthesis(null);
    try {
      const data = await historyApi.searchRepo({
        query, siteId: siteId || undefined,
        synthesis: settings.synthesisEnabled && synthesisToggle,
      });
      setRepoResults(data.snippets || []);
      setSynthesis(data.synthesis || null);
    } catch (err) { alert('Repository search failed: ' + (err?.response?.data?.error || err.message)); }
    finally { setLoadingRepo(false); }
  }

  return (
    <div style={{ padding: 24, color: C.textPrimary }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Search size={22} color={C.blue} />
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Find Relevant History</h2>
      </div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
        Describe the work you're planning. We'll surface relevant lessons from your Knowledge Base first,
        then optionally search the full document repository.
      </div>

      {/* Query box */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="e.g. hot work near boiler B-1 in zone 3"
          onKeyDown={e => { if (e.key === 'Enter') runKbSearch(); }}
          style={{ flex: 1, padding: '10px 14px', background: C.surface, border: `1px solid ${C.border}`,
                   borderRadius: 8, color: C.textPrimary, fontSize: 14, fontFamily: 'inherit' }} />
        <button onClick={runKbSearch} disabled={!query.trim() || loadingKb}
          style={{ padding: '10px 20px', borderRadius: 8, border: 'none',
                   background: query.trim() ? C.blue : C.surface, color: query.trim() ? '#fff' : C.textMuted,
                   fontSize: 13, fontWeight: 600, cursor: query.trim() ? 'pointer' : 'not-allowed',
                   fontFamily: 'inherit' }}>
          {loadingKb ? 'Searching…' : 'Search'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <select value={siteId} onChange={e => setSiteId(e.target.value)}
          style={{ padding: '6px 10px', background: C.surface, border: `1px solid ${C.border}`,
                   borderRadius: 6, color: C.textPrimary, fontSize: 12 }}>
          <option value="">— Any site —</option>
          {(sites || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type="text" value={workType} onChange={e => setWorkType(e.target.value)}
          placeholder="work type (e.g. hot_work)"
          style={{ padding: '6px 10px', background: C.surface, border: `1px solid ${C.border}`,
                   borderRadius: 6, color: C.textPrimary, fontSize: 12 }} />
      </div>

      {/* KB results */}
      {kbResults !== null && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase',
                       letterSpacing: '0.06em', marginBottom: 10 }}>
            Knowledge Base ({kbResults.length})
          </h3>
          {kbResults.length === 0 ? (
            <div style={{ padding: 16, background: C.surface, border: `1px solid ${C.border}`,
                          borderRadius: 8, fontSize: 13, color: C.textMuted }}>
              No KB entries match. Try the repository search below.
            </div>
          ) : kbResults.map(r => (
            <div key={r.id} style={{ padding: 14, background: C.surface, border: `1px solid ${C.border}`,
                                      borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.title}</div>
              <div style={{ fontSize: 12, color: C.textSecondary }}>{r.body}</div>
              {r.keywords?.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {r.keywords.map((k, i) => (
                    <span key={i} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4,
                      background: C.blue + '22', color: C.blue }}>{k}</span>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Escalation button */}
          <button onClick={runRepoSearch} disabled={loadingRepo}
            style={{ marginTop: 8, padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.blue}55`,
                     background: C.blue + '11', color: C.blue, fontSize: 12, fontWeight: 600,
                     cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ChevronRight size={12} />
            {loadingRepo ? 'Searching repository…' : 'Search Repository →'}
          </button>

          {/* Synthesis toggle (only shown if tenant has synthesis enabled) */}
          {settings.synthesisEnabled && (
            <label style={{ marginLeft: 12, fontSize: 12, color: C.textSecondary, cursor: 'pointer' }}>
              <input type="checkbox" checked={synthesisToggle} onChange={e => setSynthesisToggle(e.target.checked)}
                style={{ marginRight: 6 }} />
              <Sparkles size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3, color: C.amber }} />
              AI summary (Premium)
            </label>
          )}
        </section>
      )}

      {/* Synthesis pane */}
      {synthesis && (
        <section style={{ marginBottom: 16, padding: 16, background: C.amber + '11',
                          border: `1px solid ${C.amber}44`, borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Sparkles size={14} color={C.amber} />
            <span style={{ fontSize: 11, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              AI Summary
            </span>
          </div>
          <div style={{ fontSize: 13, color: C.textPrimary, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {synthesis}
          </div>
        </section>
      )}

      {/* Repo snippets */}
      {repoResults !== null && (
        <section>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase',
                       letterSpacing: '0.06em', marginBottom: 10 }}>
            Repository Snippets ({repoResults.length})
          </h3>
          {repoResults.length === 0 ? (
            <div style={{ padding: 16, background: C.surface, border: `1px solid ${C.border}`,
                          borderRadius: 8, fontSize: 13, color: C.textMuted }}>
              No matches in the document repository.
            </div>
          ) : repoResults.map(s => (
            <div key={s.chunkId} style={{ padding: 14, background: C.surface, border: `1px solid ${C.border}`,
                                          borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textMuted }}>
                  <span style={{ background: C.blue + '22', color: C.blue, padding: '1px 6px', borderRadius: 3,
                                 fontWeight: 700 }}>[{s.index}]</span>
                  <FileText size={11} />
                  {s.docTitle} · chunk #{s.chunkIndex + 1}
                </div>
                <button onClick={() => setPromotionTarget({
                  snippetText: s.text,
                  sourceDocId: s.docId,
                  sourceChunkId: s.chunkId,
                  userContext: { siteId },
                })}
                  style={{ background: 'none', border: `1px solid ${C.green}55`, color: C.green,
                           fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 4,
                           cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Plus size={11} /> Add to KB
                </button>
              </div>
              <div style={{ fontSize: 12, color: C.textPrimary, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {s.text}
              </div>
            </div>
          ))}
        </section>
      )}

      {promotionTarget && (
        <KBPromotionModal
          {...promotionTarget}
          sites={sites}
          zones={zones}
          onClose={() => setPromotionTarget(null)}
          onPromoted={(kbEntry) => {
            setPromotionTarget(null);
            onPromoted?.(kbEntry);
          }}
        />
      )}
    </div>
  );
}
